/**
 * Where Linear sends staff back after installing Jimi's app from Settings → Jimi
 * (the app's callback URL: <app>/settings/jimi/linear). Hands the code to the backend,
 * which has the Harness Service exchange it; the app's tokens never reach the browser.
 */
import type { ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import ApiService, { ApiError } from '@/services/api.service';
import { useApiAuthReady } from '@/hooks/useApiAuthReady';
import PageTitle from '../../components/PageTitle';

type Installed = { appUserId: string; organizationId: string };

export default function JimiLinearInstall() {
  const apiReady = useApiAuthReady();
  const [params] = useSearchParams();
  const code = params.get('code');
  const state = params.get('state');
  const denied = params.get('error');

  // A code and state work once, so this is asked exactly once.
  const done = useQuery<Installed>({
    queryKey: ['jimi', 'linear-install', state],
    queryFn: async () =>
      (await ApiService.getInstance().request('POST', '/agent-platform/jimi/linear/install/complete', { code, state })) as Installed,
    enabled: apiReady && !!code && !!state && !denied,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  let body: ReactNode;
  if (denied || !code || !state) body = <p className="text-sm text-destructive">The install wasn't completed in Linear{denied ? ` (${denied})` : ''}.</p>;
  else if (done.isLoading || !apiReady) body = <p className="text-sm text-muted-foreground">Finishing the install…</p>;
  else if (done.error) {
    const detail = done.error instanceof ApiError ? (done.error.payload as unknown as { error?: string } | undefined)?.error : undefined;
    body = <p className="text-sm text-destructive">Install failed{detail ? `: ${detail}` : '.'}</p>;
  } else body = <p className="text-sm">Jimi is installed in Linear (app user {done.data?.appUserId}).</p>;

  return (
    <div className="space-y-4 p-6">
      <PageTitle title="Install Jimi in Linear" />
      {body}
      <Link to="/settings?tab=jimi" className="text-sm underline">
        Back to Settings → Jimi
      </Link>
    </div>
  );
}
